import React, { useMemo, useRef, useCallback, useState } from 'react';

import { AxiosError } from 'axios';
import { debounce } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { LegalEntityType } from 'src/accounts/types';
import { useOutsideHandler } from 'src/common/hooks';

import { fetchLegalEntities } from '../api';

const SearchLegalEntities: React.FC = () => {
  const searchRef = useRef(null);
  const menuRef = useRef(null);
  const [searchError, setSearchError] = useState('');
  const [filteredLegalEntities, setFilteredLegalEntities] = useState<LegalEntityType[]>([]);
  const { setValue } = useFormContext();

  const fetch: (value: string) => void = async (value) => {
    try {
      const response = await fetchLegalEntities(value);
      setFilteredLegalEntities(response.data.results);
    } catch (err) {
      const error = err as AxiosError<{ detail: string }>;
      const detail = error?.response?.data?.detail;
      setSearchError(detail || 'An unknown error has occurred');
      setFilteredLegalEntities([]);
    }
  };

  const debounceHandler = useMemo(() => debounce(fetch, 1000), []);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setSearchError('');
      const text = event.target.value;
      if (!text) {
        setFilteredLegalEntities([]);
        return;
      }
      debounceHandler(text);
    },
    [debounceHandler]
  );

  const handleOnBlur = (): void => {
    setFilteredLegalEntities([]);
  };

  const handleSetLegalEntity = (legal_entity: LegalEntityType) => () => {
    setFilteredLegalEntities([]);
    setValue('legal_entity', legal_entity.id);
  };

  useOutsideHandler(menuRef, handleOnBlur);

  return (
    <div className="relative" ref={menuRef}>
      <label htmlFor="search-legal-entity" className="input-label">
        Search for Legal Entities
        <input
          name="search-legal-entity"
          ref={searchRef}
          className="input w-full mt-1 transition duration-150 ease-in-out"
          placeholder="Search by name"
          data-testid="legal-entity-search"
          onChange={handleOnChange}
          type="text"
          autoComplete="off"
        />
        {searchError && <p className="error-text">{searchError}</p>}
      </label>
      <div
        className="absolute left-0 bg-white-0.0 min-w-96 mt-2 origin-top-right rounded shadow-lg z-50 border border-white-1.0 max-h-80 overflow-auto"
        hidden={filteredLegalEntities.length === 0}
      >
        {filteredLegalEntities.map((legal_entity) => (
          <div
            key={legal_entity.id}
            className="p-4 border-b border-white-1.0 hover:bg-white-1.0 cursor-pointer"
            role="presentation"
            onClick={handleSetLegalEntity(legal_entity)}
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            data-testid={`legal-entity-${legal_entity.id}`}
          >
            {legal_entity.name}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SearchLegalEntities;
